import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearUserAuditLog } from 'dashboard/redux/actions/user-audit-log';
import { RootState } from 'dashboard/redux/store';
import { loadUserAuditLog } from 'dashboard/redux/thunk-actions/user-audit-log';
import DashboardUserAuditLogsShowView from 'dashboard/views/DashboardUserAuditLogsShowView';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type Params = { userAuditLogId: string };

const DashboardUserAuditLogsShowRoute = (props: PropsFromRedux) => {
  const { userAuditLogId } = useParams<Params>() as Params; // userAuditLogId will always be present on this route
  useOnMountAndUnmount(props.clearUserAuditLog);

  useRedirectOnEnvironmentChange('/dashboard/user-audit-logs');

  const { loading: userAuditLogLoading } = useApiLoader({
    loader: () => props.loadUserAuditLog(userAuditLogId),
    deps: [props.loadUserAuditLog, userAuditLogId, props.showMessage],
    onNetworkError: () => props.showMessage('Failed to load user audit log.', MessageType.Failure),
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load user audit log.', MessageType.Failure);
      }
    },
  });

  return (
    <DashboardUserAuditLogsShowView
      userAuditLog={props.userAuditLog}
      user={props.user}
      loading={userAuditLogLoading && props.userAuditLog == null}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  userAuditLog: state.userAuditLog.userAuditLog,
  user: state.userAuditLog.user,
});

const mapDispatchToProps = {
  loadUserAuditLog,
  clearUserAuditLog,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardUserAuditLogsShowRoute);
